import React, { useContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';

// Store
import { Dispatch, RootState } from '../../store/store';

// Types
import { IActionsDropdown, IActionsTag } from '../../types/tableDropdown';

// Constants
import ROUTE from '../../constants/route-constants';

// Components
import Table from '../../components/Table/Table';
import IconEditOutlined from '../../components/Icons/IconEditOutlined/IconEditOutlined';
import { ElementSize } from '../../types/element-size';
import IconSwitcher from '../../components/Icons/IconSwitcher/IconSwitcher';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import { ACTION_BY_PAGE, CYCLE_MEASUREMENT_SYMBOL, PAGE_TITLE } from '../../constants/common';
import { useTranslation } from 'react-i18next';
import Tooltip from '../../components/Tooltip';
import { CONNECTION_KEY } from '../../components/Table/contrants';
import { AbilityContext } from '../../components/Can/Can';
import { Action } from '../../enums/action';

type ControllersViewProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const ActivityIcon = ({ active }: { active: boolean }) => {
  const activityStyles = useMemo(() => {
    if (active) {
      return 'text-success';
    }

    return 'text-danger';
  }, [active]);
  return (
    <div className={`flex items-center justify-center ${activityStyles}`}>
      <IconSwitcher size={ElementSize.XXS} />
    </div>
  );
};

const ControllersView: React.FC<ControllersViewProps> = ({ controllers, getControllers, updateController }) => {
  const navigate = useNavigate();
  const [isControllersLoaded, setIsControllersLoaded] = useState(false);
  const { t } = useTranslation();
  const ability = useContext(AbilityContext);

  const fetchControllers = async () => {
    await getControllers();

    setIsControllersLoaded(true);
  };

  useEffect(() => {
    fetchControllers();
  }, []);

  const controllersView = controllers.map((controller) => ({
    Name: controller?.name,
    [CONNECTION_KEY]: controller.connected,
    ['Serial Number']: controller?.serial_number,
    Active: controller?.is_active,
    Client: controller?.location?.name || 'No client',
    Units: controller?.units ? CYCLE_MEASUREMENT_SYMBOL[controller.units] : '',
    IP: controller?.ip_address || '-',
    Version: controller?.firmware_version || '-',
    id: controller?.id,
  }));

  const actionsButtons: IActionsDropdown[] = [
    {
      icon: () => (
        <Tooltip hint={t('commonText.edit')} >
          <IconEditOutlined stroke="inherit" iconClassName="text-inherit" size={ElementSize.XXS} />
        </Tooltip>
      ),
      onClick: (id: string) => {
        const path = generatePath(ROUTE.EDIT_CONTROLLER, { id });
        navigate(path);
      },
      loading: false,
      tag: IActionsTag.Link,
    },
    {
      icon: (item) => (
        <Tooltip hint={(item.Active) ? t('commonText.deactivate') : t('commonText.activate')} hintClassName='bottom-7 right-[-120%]' isCustomSettings >
          <ActivityIcon active={item.Active} />
        </Tooltip>
      ),
      onClick: async (id: string, payload) => {
        const formData = {
          is_active: !payload.Active,
        };
        await updateController({
          id,
          formData,
        });
        await fetchControllers();
      },
      isShow: () => ability.can(Action.Update, ACTION_BY_PAGE[PAGE_TITLE.CONTROLLERS].CHANGE_ACTIVE_STATUS),
      loading: false,
    },
  ];

  return (
    <>
      <div className="p-controllers-view flex h-full w-full flex-col">
        {!isControllersLoaded && (
          <div className="flex flex-auto items-center justify-center">
            <SpinnerLoader />
          </div>
        )}
        {isControllersLoaded && (
          <>
            <h2 className="mb-5 text-title-md2 font-semibold text-black dark:text-white">Controllers</h2>
            <Table
              values={controllersView}
              defaultKeys={['Name', 'Connection', 'Serial Number', 'Units', 'Active', 'Client', 'IP', 'Version']}
              actionsButtons={actionsButtons}
              defaultFieldSort='Name'
            />
          </>
        )}
      </div>
    </>
  );
};

const mapState = (state: RootState) => ({
  controllers: state.controllers.controllers_list,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getControllers: dispatch.controllers.getControllers,
  updateController: dispatch.controllers.updateController,
});

export default connect(mapState, mapDispatch)(ControllersView);
